import { Application } from '@/types/database.types';
import { OtherDocument, STRING_FIELDS, ARRAY_FIELDS } from './types';
import { uploadFile } from '@/utils/fileUpload';

export async function handleDocumentUpload(
  file: File,
  field: string,
  data: Partial<Application>,
  otherDocuments: OtherDocument[],
  onSave: (data: Partial<Application>) => Promise<void>,
  updateOtherDocuments?: (docs: OtherDocument[]) => void,
  maxOtherDocuments?: number
) {
  const { url } = await uploadFile(file);

  if (field === 'other') {
    if (maxOtherDocuments && otherDocuments.length >= maxOtherDocuments) {
      throw new Error(`Maximum of ${maxOtherDocuments} additional documents allowed`);
    }
    
    const newOtherDocuments = [
      ...otherDocuments,
      { name: file.name, url }
    ];
    
    if (updateOtherDocuments) {
      updateOtherDocuments(newOtherDocuments);
    }
    
    // Use a safe type conversion
    await onSave({ 
      other_documents: JSON.stringify(newOtherDocuments) as unknown as string[] 
    });
    
    return;
  } 
  
  if (STRING_FIELDS.includes(field)) {
    // For string fields, assign the URL directly as a string
    await onSave({ [field]: url } as Partial<Application>);
  } else if (field === 'other_documents') {
    // Special handling for other_documents which is stored as a JSON string
    let currentOtherDocs: any[] = [];
    
    // Handle existing documents
    if (data.other_documents) {
      if (typeof data.other_documents === 'string') {
        try {
          currentOtherDocs = JSON.parse(data.other_documents);
        } catch (e) {
          console.error('Error parsing other_documents', e);
          currentOtherDocs = [];
        }
      } else if (Array.isArray(data.other_documents)) {
        currentOtherDocs = data.other_documents;
      }
    }
    
    // Add the new document URL
    currentOtherDocs.push(url);
    
    // Convert to string for storage and use type assertion to ensure compatibility
    const jsonString = JSON.stringify(currentOtherDocs);
    await onSave({ 
      other_documents: jsonString as unknown as string[]
    });
  } else if (ARRAY_FIELDS.includes(field)) {
    // For fields that expect string arrays, wrap the URL in an array
    // Create a properly typed update object with correct array values
    const updateData: Partial<Application> = {};
    
    // Get existing array or create new one
    let existingArray: string[] = [];
    if (data[field as keyof typeof data]) {
      const existingValue = data[field as keyof typeof data];
      if (Array.isArray(existingValue)) {
        existingArray = existingValue as string[];
      } else if (typeof existingValue === 'string') {
        // If it's a string, try parsing it as JSON
        try {
          existingArray = JSON.parse(existingValue);
        } catch (e) {
          existingArray = [existingValue as string];
        }
      }
    }
    
    // Add the new URL to the array
    const updatedArray = [...existingArray, url];
    
    // Use type assertion to set the field with correct array type
    (updateData as any)[field] = updatedArray;
    
    await onSave(updateData);
  } else {
    // For any other fields, use proper typing based on existing data
    const updateData: Partial<Application> = {};
    
    // Check if the field already has a value and what type it is
    const existingValue = data[field as keyof typeof data];
    if (Array.isArray(existingValue)) {
      // If it's already an array, append to it
      (updateData as any)[field] = [...existingValue, url];
    } else {
      // Otherwise treat as a string field
      (updateData as any)[field] = url;
    }
    
    await onSave(updateData);
  }
}
